import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Label } from '../../components/ui/label'
import { PasswordInput } from '../../components/ui/password-input'
import { PasswordRequirements } from '../../components/ui/password-requirements'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from '../../lib/password-rules'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { supabase } from '../../lib/supabase'
// useAuth is not directly used in this component after refactor, but keeping for potential future use or if other parts depend on it.
// import { useAuth } from '../../lib/auth.context'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'
import { processAuthLink, cleanupAuthLinkUrl, getRedirectUrlBasedOnRole } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api'
import { env } from '../../lib/env'

export function CompleteInvitePage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [showTOTP, setShowTOTP] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [redirectTo, setRedirectTo] = useState('/admin/dashboard')
  const navigate = useNavigate()
  const { toast } = useToast()
  // const { getToken } = useAuth(); // getToken is not used directly here anymore

  // Handle auth link processing when component mounts
  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[CompleteInvitePage] useEffect - handleAuthFlow called')
      setIsLoading(true) // Set loading while processing the link

      try {
        // Pass `location` from `useLocation()` hook
        const result = await processAuthLink(supabase, window.location, 'invite')

        if (result.success && result.session) {
          console.log('[CompleteInvitePage] Successfully authenticated via processAuthLink. Session:', result.session)
          setHasAccessToken(true)
          cleanupAuthLinkUrl(window.location, false) // Clear hash and 'code' param
        } else {
          console.log('[CompleteInvitePage] Authentication failed via processAuthLink. Error:', result.error)
          toast({
            title: 'Error',
            description: result.error?.message || 'Invalid or expired invite link. Please request a new invitation.',
            variant: 'destructive',
          })
          navigate('/auth/login', { replace: true })
        }
      } catch (error) {
        // Catch unexpected errors from processAuthLink
        console.error('[CompleteInvitePage] Unexpected error in handleAuthFlow:', error)
        toast({
          title: 'Error',
          description: 'Failed to process invite link. Please try again.',
          variant: 'destructive',
        })
        navigate('/auth/login', { replace: true })
      } finally {
        setIsLoading(false) // Clear loading state
      }
    }

    handleAuthFlow()
  }, [navigate, toast, supabase]) // Added supabase to dependencies, location is implicitly from window.location

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!hasAccessToken) {
      setFormError('Invalid session. Please use a valid invite link.')
      return
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    // Validate password meets all requirements
    if (!isPasswordValid(password)) {
      setFormError(PASSWORD_REQUIREMENTS_MESSAGE)
      return
    }

    setIsLoading(true)
    try {
      // Get the current session to verify it's still valid
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Your session has expired. Please use a new invite link.')
      }

      // Update the password
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        throw error
      }

      toast({
        title: 'Password Set',
        description:
          'Password has been set successfully. Please complete the security setup by configuring two-factor authentication.',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()

      // Determine redirect URL using the utility function
      const newRedirectTo = getRedirectUrlBasedOnRole(userProfile, env)

      // All users must complete TOTP enrollment
      setRedirectTo(newRedirectTo)
      setShowTOTP(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to set password'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })

      // If we get a session error, redirect to login
      if (error.message?.includes('session')) {
        navigate('/auth/login')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {showTOTP ? (
        <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          <TOTPEnrollment redirectTo={redirectTo} />
        </div>
      ) : (
        <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          <div className="flex min-h-[500px]">
            {/* Left Panel - Marketing Content */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}
              />
              {/* Header */}
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
                <div className="flex items-center">
                  <img src="/fanfairreversed.png" alt="FanFair" className="h-8" />
                </div>
              </div>
              {/* Content */}
              <div className="relative z-10 flex items-center justify-center p-12">
                <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
                  <h1 className="text-3xl font-bold mb-4 text-white">Capturing Moments, Creating Memories</h1>
                  <p className="text-lg opacity-90 mb-8 text-white">
                    Welcome to FanFair. Set your password to activate your Admin Portal account.
                  </p>
                  {/* Slide indicators */}
                  <div className="flex justify-center space-x-2">
                    <div className="w-8 h-1 bg-white rounded-full"></div>
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                    <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Right Panel - Registration Form */}
            <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
              <div className="w-full max-w-sm space-y-8">
                {/* Mobile Header */}
                <div className="lg:hidden text-center">
                  <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
                </div>
                <div className="space-y-2">
                  <h1 className="text-2xl font-medium text-white">Complete Your Registration</h1>
                  <p className="text-slate-400 font-light">Set a password to activate your account.</p>
                </div>

                {isLoading ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-slate-400 font-light">Processing invitation...</p>
                  </div>
                ) : !hasAccessToken ? (
                  <Alert variant="destructive">
                    <AlertDescription className="text-white">
                      Invalid invite link. Please request a new invitation.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {formError && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertDescription className="text-white">{formError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-password" className="text-sm font-light text-white">
                          New Password
                        </Label>
                        <PasswordInput
                          id="new-password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isLoading}
                          className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                        />
                        <PasswordRequirements password={password} className="text-slate-400" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-sm font-light text-white">
                          Confirm Password
                        </Label>
                        <PasswordInput
                          id="confirm-password"
                          placeholder="Confirm your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={isLoading}
                          className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Setting Password...' : 'Set Password'}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
